// Resolves the name a client gave an aisle, out of the store layout.
//
// Clients name their aisles freely while the pipeline stores raw placements
// (`3J`, `W`, `1`). The layout names them at two levels, and each has its own
// reader here:
//
//   resolveAisleName   the aisle as a whole   ->  screens listing aisles
//   resolveLinearName  one side of an aisle   ->  screens listing linears
//
// A linear is keyed by its composed identifier (`3J-W-1`); callers holding the
// three placement columns build it with buildBackendAisleName first.
//
// Both readers answer null when the layout names nothing, never the raw
// identifier: the caller owns the fallback, and returning the raw id here would
// make "translated" and "untranslated" indistinguishable. This mirrors what
// api-tasks resolves server-side, so both always agree.
//
// TODO: migrate to @zippeditoolsjs/utils once that package exists, instead of
// copying these resolvers into each repo. They are pure and their domain (the
// structure of aisles) is common to every Zippedi product, not specific to Task
// Manager. Prerequisite: packages/mobile does not consume the @zippeditoolsjs
// scope yet — only web does — so the migration has to add it there too, or the
// resolver ends up split across platforms.

/** Coerce a placement value to the string the layout keys by, or null. */
function normalize(value) {
  if (value === null || value === undefined) {
    return null;
  }

  const text = String(value).trim();
  return text.length > 0 ? text : null;
}

/** True when the layout is a usable dictionary of aisles. */
function isUsableLayout(aislesLayout) {
  // The slice stores [] when the layout request comes back empty.
  return (
    !!aislesLayout &&
    typeof aislesLayout === 'object' &&
    !Array.isArray(aislesLayout)
  );
}

/**
 * Read the client name off a layout node, or null when it names nothing.
 *
 * A name equal to the key it is stored under is not a name: it is the raw
 * identifier echoed back. api-tasks discards those server-side, and accepting
 * one here would present a raw id as a translation.
 */
function clientNameOf(node, rawKey) {
  if (!node || typeof node !== 'object') {
    return null;
  }

  const name = node.client_name;
  if (typeof name !== 'string' || name.trim().length === 0) {
    return null;
  }

  return name === rawKey ? null : name;
}

/**
 * Compose the identifier the layout keys a linear by, e.g. `3J-W-1`.
 *
 * Only the parts a task actually carries are joined, so a placement with no
 * orientation composes `3J` rather than `3J-undefined`.
 *
 * @param {string|number|null|undefined} aisle Raw aisle.
 * @param {string|number|null|undefined} orientation Raw orientation.
 * @param {string|number|null|undefined} aislePart Raw aisle part.
 * @returns {string|null} The composed identifier, or null with no aisle.
 */
export function buildBackendAisleName(aisle, orientation, aislePart) {
  const parts = [aisle, orientation, aislePart].map(normalize);
  if (parts[0] === null) {
    return null;
  }

  return parts.filter(part => part !== null).join('-');
}

/**
 * Resolve the name the client gave a whole aisle.
 *
 * @param {Object|null|undefined} aislesLayout The `capture` layer, as stored.
 * @param {string|number|null|undefined} aisle Raw aisle.
 * @returns {string|null} The client name, or null when the aisle has none.
 */
export function resolveAisleName(aislesLayout, aisle) {
  const key = normalize(aisle);
  if (!key || !isUsableLayout(aislesLayout)) {
    return null;
  }

  return clientNameOf(aislesLayout[key], key);
}

/**
 * Resolve the name the client gave a linear, one side of an aisle.
 *
 * Never falls back to the name of the whole aisle: that would label one side of
 * a shelf with the name of all of it. The layout nests linears inside their
 * aisle, and the aisle is the first segment of the identifier, which is safe
 * because an aisle name never contains a hyphen.
 *
 * @param {Object|null|undefined} aislesLayout The `capture` layer, as stored.
 * @param {string|null|undefined} backendAisleName Composed identifier, `3J-W-1`.
 * @returns {string|null} The client name, or null when the linear has none.
 */
export function resolveLinearName(aislesLayout, backendAisleName) {
  const linear = normalize(backendAisleName);
  if (!linear || !isUsableLayout(aislesLayout)) {
    return null;
  }

  const aisle = aislesLayout[linear.split('-')[0]];
  return aisle ? clientNameOf(aisle[linear], linear) : null;
}
